import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLogin, useLogout, useSession } from '@/lib/auth/session';
import { useSafeAccess } from '@/lib/access';
import { useSites } from '@/lib/queries';
import { sitePath } from '@/pages/site/siteMenu';
import { JOURNAL_TYPES } from '@/pages/safe/safeJournalApi';
import type { DeviceChallenge } from '@/lib/types';
import { DeviceVerifyModal } from '@/pages/login/DeviceVerifyModal';
import { PasswordResetModal } from '@/pages/login/PasswordResetModal';

/*
  보미건설 안전 홈페이지 — 첫 화면(관문). 좌우 둘로 나눈다(사용자 결정 2026-10-27 「좌우 분할 + 현장 사진」):
  왼쪽은 짙은 남색 판에 로고 · 이름과 현장 선화(사진이 들어올 자리), 오른쪽은 로그인 카드.

  로그인해도 화면을 옮기지 않는다 — 카드가 「○○님, 환영합니다」 와 바로가기(대시보드 · 일지 넷)로 바뀌고, 어디로 갈지는
  사람이 누른다. 권한(`safe`)이 없는 계정에게는 바로가기가 흑백이다. 문지기에 돌아온 사람(`?returnTo=`)만 곧장 간다.

  계정은 그룹웨어와 같다(`/v1/auth/login`) — 세션 쿠키는 호스트 전용이라 이 주소에서 한 번 더 들어온다.
*/
const HOME_URL = 'https://www.bomienc.com';
const LOGIN_EMAIL_KEY = 'bomi-safe:login-email';

const DOORS: Array<{ to: string; label: string }> = [
  { to: '/dashboard', label: '대시보드' },
  { to: JOURNAL_TYPES.EDUCATION.path, label: JOURNAL_TYPES.EDUCATION.label },
  { to: JOURNAL_TYPES.HEALTH.path, label: JOURNAL_TYPES.HEALTH.label },
  { to: JOURNAL_TYPES.PATROL.path, label: JOURNAL_TYPES.PATROL.label },
  { to: JOURNAL_TYPES.JOINT.path, label: JOURNAL_TYPES.JOINT.label },
];

/** 열린 리다이렉트를 막는다. `//evil.com` 같은 값은 스킴 없는 절대 URL 이므로 함께 걸러낸다. */
function safeReturnPath(returnTo: string | null): string | null {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return null;
  return returnTo;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const login = useLogin();
  const logout = useLogout();

  const me = session.data?.authenticated ? session.data : null;
  // 안전 권한이면 대시보드 · 일지, 배정 현장만 보는 사람은 자기 현장들이 바로가기다(DOC-136 §6). 둘 다 없으면 흑백.
  const access = useSafeAccess();
  const sites = useSites(me !== null);
  const mySites = (sites.data ?? []).filter((site) => access.siteIds.includes(site.id));
  const doors = access.all || mySites.length === 0 ? DOORS : mySites.map((site) => ({ to: sitePath(site.id), label: site.name }));
  const allowed = me !== null && (access.all || mySites.length > 0);
  /** 할일 링크 · 옛 주소로 들어오다 문지기에게 돌아온 사람은 로그인 뒤 그 자리로 곧장 간다. */
  const returnTo = safeReturnPath(searchParams.get('returnTo'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(true);
  const [showReset, setShowReset] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** 비밀번호 다음 한 걸음(로그인 OTP · 새 브라우저 본인확인). 있으면 창을 띄운다. */
  const [challenge, setChallenge] = useState<{ value: DeviceChallenge; email: string } | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (me === null) emailRef.current?.focus();
  }, [me]);

  useEffect(() => {
    const saved = window.localStorage.getItem(LOGIN_EMAIL_KEY);
    if (saved) setEmail(saved);
  }, []);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (login.isPending) return;
    setNotice(null);
    const trimmed = email.trim();
    login.mutate(
      { email: trimmed, password },
      {
        onSuccess: (result) => {
          if (result.deviceChallenge) {
            setChallenge({ value: result.deviceChallenge, email: trimmed });
            return;
          }
          finishLogin(trimmed);
        },
      },
    );
  }

  /** 세션이 열린 뒤 — 바로 로그인했든 본인확인을 마쳤든 같은 손으로 마무리한다. */
  function finishLogin(trimmed: string) {
    if (rememberEmail) window.localStorage.setItem(LOGIN_EMAIL_KEY, trimmed);
    else window.localStorage.removeItem(LOGIN_EMAIL_KEY);
    setPassword('');
    setChallenge(null);
    if (returnTo) navigate(returnTo, { replace: true });
  }

  /** 로그아웃 — 관문에 머문다. 로그인 카드로 되돌아가는 것이 곧 로그아웃한 모습이다. */
  function onLogout() {
    setNotice(null);
    logout.mutate();
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row" style={{ background: 'var(--bg-card)' }}>
      <style>{STYLES}</style>
      <section className="sl-hero" aria-label="보미건설 안전관리">
        <div className="sl-grid" aria-hidden />
        <Skyline />
        <div className="relative flex items-center gap-3">
          <img src="/brand/bomi-ci.png" alt="BoMI" className="sl-logo" />
          <span className="sl-tag">SAFE</span>
        </div>
        <h1 className="sl-title">
          보미건설
          <br />
          {/* 서비스 이름 — 앱 「보미건설 안전관리」 와 같은 글자다(DOC-141 · 사용자 결정 2026-10-29). */}
          안전관리
        </h1>
        <a className="sl-home" href={HOME_URL} target="_blank" rel="noopener noreferrer">
          www.bomienc.com ↗
        </a>
      </section>

      <main className="flex flex-1 items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full" style={{ maxWidth: 400 }}>
          {notice !== null && (
            <p className="mb-4 text-ui-xs" style={{ color: 'var(--accent)' }}>
              {notice}
            </p>
          )}

          {me !== null ? (
            <div>
              <div className="mb-6 flex items-center gap-4">
                <span
                  className="flex shrink-0 items-center justify-center rounded-full text-xl font-semibold text-white"
                  style={{ width: 56, height: 56, background: 'var(--accent)' }}
                  aria-hidden
                >
                  {me.user?.name.slice(0, 1)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xl font-semibold">{me.user?.name}님, 환영합니다.</span>
                  {me.user?.rank && (
                    <span className="block truncate text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                      {me.user.rank}
                    </span>
                  )}
                </span>
              </div>

              <nav className="flex flex-col gap-2" aria-label="바로가기">
                {doors.map((door) => (
                  <button
                    key={door.to}
                    type="button"
                    className={`sl-door ${allowed ? '' : 'sl-door-off'}`}
                    disabled={!allowed}
                    onClick={() => navigate(door.to)}
                  >
                    {door.label}
                    <span aria-hidden>→</span>
                  </button>
                ))}
              </nav>

              <button
                type="button"
                className="btn-secondary mt-6 w-full justify-center"
                disabled={logout.isPending}
                onClick={onLogout}
              >
                로그아웃
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <h2 className="mb-8 text-2xl font-bold">로그인</h2>

              <label className="mb-4 block">
                <span className="mb-1.5 block text-ui-sm font-medium">아이디</span>
                <input
                  ref={emailRef}
                  className="input-field sl-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  required
                />
              </label>

              <label className="mb-4 block">
                <span className="mb-1.5 block text-ui-sm font-medium">비밀번호</span>
                <input
                  type="password"
                  className="input-field sl-input"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>

              <div className="mb-6 flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-ui-sm">
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(event) => setRememberEmail(event.target.checked)}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>아이디 저장</span>
                </label>
                <button
                  type="button"
                  className="text-ui-sm"
                  style={{ color: 'var(--text-secondary)' }}
                  onClick={() => {
                    setNotice(null);
                    setShowReset(true);
                  }}
                >
                  비밀번호 찾기
                </button>
              </div>

              {login.isError && (
                <p className="mb-4 text-ui-xs" style={{ color: 'var(--danger)' }}>
                  {login.error.message}
                </p>
              )}

              <button type="submit" className="btn-primary sl-submit w-full justify-center" disabled={login.isPending}>
                {login.isPending ? '확인 중입니다…' : '로그인'}
              </button>
            </form>
          )}
        </div>
      </main>

      {challenge && (
        <DeviceVerifyModal
          challenge={challenge.value}
          onVerified={() => finishLogin(challenge.email)}
          onClose={() => setChallenge(null)}
        />
      )}
      {showReset && (
        <PasswordResetModal
          onClose={() => setShowReset(false)}
          onDone={() => {
            setShowReset(false);
            setPassword('');
            setNotice('비밀번호를 변경했습니다. 새 비밀번호로 로그인해 주세요.');
          }}
        />
      )}
    </div>
  );
}

/** 왼쪽 판 아래의 현장 선화 — 건물 몇 채와 타워크레인. 사진을 넣을 때까지의 자리다. */
function Skyline() {
  return (
    <svg className="sl-skyline" viewBox="0 0 600 260" preserveAspectRatio="xMidYMax slice" aria-hidden>
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M0 260V190h60v70M60 260V150h70v110M130 260V200h40v60M300 260V120h80v140M380 260V170h50v90M430 260V140h70v120M500 260V200h100v60" />
        <path
          d="M70 165h50M70 185h50M70 205h50M310 140h60M310 165h60M310 190h60M310 215h60M440 160h50M440 185h50M440 210h50"
          opacity="0.5"
        />
        <path d="M215 260V40M205 260V40M205 40h10M205 70l10 20M215 70l-10 20M205 110l10 20M215 110l-10 20M205 150l10 20M215 150l-10 20M205 190l10 20M215 190l-10 20" />
        <path d="M150 40h230M150 52h230M150 40l10 12M380 40l-10 12M210 40V18M210 18l-60 22M210 18l170 22" />
        <path d="M330 52v70M322 122h16" />
        <rect x="150" y="52" width="26" height="18" />
      </g>
    </svg>
  );
}

const STYLES = `
.sl-hero { position: relative; overflow: hidden; display: flex; flex-direction: column; gap: 20px; padding: 32px 28px 170px;
  color: #fff; background: linear-gradient(160deg, #0b1a33 0%, #12305a 55%, #1e4a7a 100%); }
.sl-grid { position: absolute; inset: 0; opacity: 0.08;
  background-image: linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px);
  background-size: 40px 40px; mask-image: linear-gradient(180deg, #000 0%, transparent 75%); }
.sl-skyline { position: absolute; left: 0; right: 0; bottom: 0; width: 100%; height: 45%; color: rgba(255,255,255,0.22); }
.sl-logo { height: 28px; width: auto; filter: brightness(0) invert(1); }
.sl-tag { font-size: 12px; font-weight: 700; letter-spacing: 0.2em; padding: 3px 10px; border: 1px solid rgba(255,255,255,0.4); border-radius: 999px; }
.sl-title { position: relative; margin: 0; font-size: 30px; line-height: 1.25; font-weight: 800; letter-spacing: -0.02em; }
.sl-home { position: relative; font-size: 13px; color: rgba(255,255,255,0.7); }
.sl-home:hover { color: #fff; }
.sl-input { padding: 11px 14px; font-size: 15px; }
.sl-submit { padding: 12px 16px; font-size: 15px; font-weight: 600; }
.sl-door { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border: 1px solid var(--border);
  border-radius: 10px; background: var(--bg-card); font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.sl-door span { color: var(--accent); transition: transform 0.15s; }
.sl-door:hover { border-color: var(--accent); background: var(--accent-bg); }
.sl-door:hover span { transform: translateX(3px); }
.sl-door-off { opacity: 0.4; filter: grayscale(1); cursor: default; }
.sl-door-off:hover { border-color: var(--border); background: var(--bg-card); }
@media (min-width: 1024px) {
  .sl-hero { width: 52%; min-height: 100vh; padding: 48px 56px; }
  .sl-title { font-size: 48px; margin-top: auto; margin-bottom: 4px; }
  .sl-home { margin-bottom: 38vh; }
  .sl-skyline { height: 42%; }
}
`;
